/**
 * Base Image Discovery Tab — admin dashboard for managing base image
 * auto-discovery, proposals, and RAPIDS version tracking.
 *
 * Sections:
 * 1. Active Configuration (base images + RAPIDS matrix)
 * 2. Pending Proposals (base image + RAPIDS, with approve/reject)
 * 3. Scan History (collapsible)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, ChevronRight, AlertCircle } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ActiveImage {
  id: number;
  provider: string;
  image_type: string;
  config: Record<string, unknown>;
  activated_from_proposal_id: number | null;
  activated_by: number | null;
  activated_at: string | null;
  last_scan_at: string | null;
  is_stale: boolean;
  created_at: string;
  updated_at: string;
}

interface ActiveRapidsVersion {
  id: number;
  rapids_version: string;
  compatible_python: string[];
  compatible_cuda: string[];
  is_default: boolean;
  activated_from_proposal_id: number | null;
  activated_by: number | null;
  created_at: string;
  updated_at: string;
}

interface Proposal {
  id: number;
  provider: string;
  image_type: string;
  config: Record<string, unknown>;
  diff_summary: string | null;
  status: string;
  scan_id: number;
  discovered_at: string;
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
}

interface RapidsProposal {
  id: number;
  rapids_version: string;
  compatible_python: string[];
  compatible_cuda: string[];
  is_new_version: boolean;
  diff_summary: string | null;
  status: string;
  scan_id: number;
  discovered_at: string;
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
}

interface Scan {
  id: number;
  triggered_by: number | null;
  trigger_type: string;
  providers_scanned: string[];
  scan_rapids: boolean;
  status: string;
  proposals_created: number;
  rapids_proposals_created: number;
  errors: Record<string, string | null> | null;
  consistency_warnings: string[] | null;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
}

interface ActiveConfigResponse {
  images: ActiveImage[];
  rapids_versions: ActiveRapidsVersion[];
  latest_scan: Scan | null;
  pending_proposals: number;
  pending_rapids_proposals: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAuthHeaders() {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
}

function configSummary(config: Record<string, unknown>, provider: string): string {
  if (provider === 'aws') {
    return (config.filter as string) || '—';
  }
  if (provider === 'gcp') {
    return (config.family as string) || '—';
  }
  if (provider === 'azure') {
    return `${config.publisher}/${config.offer}/${config.sku}`;
  }
  return JSON.stringify(config).slice(0, 80);
}

function statusBadge(status: string): string {
  switch (status) {
    case 'COMPLETED': return 'text-green-400';
    case 'PARTIAL': return 'text-amber-400';
    case 'FAILED': return 'text-red-400';
    case 'RUNNING': return 'text-blue-400';
    default: return 'text-gray-400';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BaseImageDiscoveryTab() {
  const [activeConfig, setActiveConfig] = useState<ActiveConfigResponse | null>(null);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [rapidsProposals, setRapidsProposals] = useState<RapidsProposal[]>([]);
  const [scans, setScans] = useState<Scan[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [confirmAction, setConfirmAction] = useState<{
    type: 'approve' | 'reject';
    target: 'image' | 'rapids';
    id: number;
    label: string;
  } | null>(null);
  const [setAsDefault, setSetAsDefault] = useState(false);

  const api = getApiUrl();

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [configRes, proposalsRes, rapidsRes, scansRes] = await Promise.all([
        axios.get<ActiveConfigResponse>(`${api}/api/v1/base-image-discovery/active`, { headers: getAuthHeaders() }),
        axios.get<Proposal[]>(`${api}/api/v1/base-image-discovery/proposals?status=PENDING`, { headers: getAuthHeaders() }),
        axios.get<RapidsProposal[]>(`${api}/api/v1/base-image-discovery/rapids-proposals?status=PENDING`, { headers: getAuthHeaders() }),
        axios.get<Scan[]>(`${api}/api/v1/base-image-discovery/scans?limit=10`, { headers: getAuthHeaders() }),
      ]);
      setActiveConfig(configRes.data);
      setProposals(proposalsRes.data);
      setRapidsProposals(rapidsRes.data);
      setScans(scansRes.data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const triggerScan = async () => {
    setScanning(true);
    setError(null);
    try {
      const res = await axios.post<Scan>(
        `${api}/api/v1/base-image-discovery/scan`,
        { providers: ['aws', 'gcp', 'azure'], scan_rapids: true },
        { headers: getAuthHeaders() },
      );
      // Poll until completed
      const scanId = res.data.id;
      let attempts = 0;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 2000));
        const check = await axios.get<{ status: string }>(
          `${api}/api/v1/base-image-discovery/scans/${scanId}`,
          { headers: getAuthHeaders() },
        );
        if (check.data.status !== 'RUNNING') break;
        attempts++;
      }
      await loadAll();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Scan failed';
      setError(msg);
    } finally {
      setScanning(false);
    }
  };

  const executeAction = async () => {
    if (!confirmAction) return;
    const { type, target, id } = confirmAction;

    try {
      if (target === 'image') {
        const url = `${api}/api/v1/base-image-discovery/proposals/${id}/${type}`;
        await axios.post(url, { notes: reviewNotes || undefined }, { headers: getAuthHeaders() });
      } else {
        const url = `${api}/api/v1/base-image-discovery/rapids-proposals/${id}/${type}`;
        const body = type === 'approve'
          ? { set_as_default: setAsDefault, notes: reviewNotes || undefined }
          : { notes: reviewNotes || undefined };
        await axios.post(url, body, { headers: getAuthHeaders() });
      }
      setConfirmAction(null);
      setReviewNotes('');
      setSetAsDefault(false);
      await loadAll();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Action failed';
      setError(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <Loader2 className="w-5 h-5 mr-3 animate-spin" />
        Loading base image configuration...
      </div>
    );
  }

  const pendingCount = (activeConfig?.pending_proposals ?? 0) + (activeConfig?.pending_rapids_proposals ?? 0);

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 text-red-300 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Section 1: Active Configuration                                     */}
      {/* ------------------------------------------------------------------ */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-medium text-white">Active Configuration</h3>
            {activeConfig?.latest_scan && (
              <span className="text-xs text-gray-500">
                Last scan: {formatDate(activeConfig.latest_scan.completed_at)}
              </span>
            )}
            {pendingCount > 0 && (
              <Badge className="bg-amber-900/50 text-amber-300 border-amber-800">
                {pendingCount} pending
              </Badge>
            )}
          </div>
          <Button
            size="sm"
            onClick={triggerScan}
            disabled={scanning}
          >
            {scanning ? <><Loader2 className="w-4 h-4 animate-spin" /> Scanning...</> : 'Scan Now'}
          </Button>
        </div>

        {/* Base Images Table */}
        <Card className="overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-400">
                <th className="text-left px-4 py-2 font-medium">Provider</th>
                <th className="text-left px-4 py-2 font-medium">Image Type</th>
                <th className="text-left px-4 py-2 font-medium">Key Config</th>
                <th className="text-left px-4 py-2 font-medium">Last Scanned</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {activeConfig?.images.map(img => (
                <tr
                  key={img.id}
                  className={`border-b border-gray-800/50 transition-colors ${img.is_stale ? 'bg-amber-900/10' : 'hover:bg-gray-800/30'}`}
                >
                  <td className="px-4 py-2 text-gray-300 uppercase text-xs font-mono">{img.provider}</td>
                  <td className="px-4 py-2 text-gray-200">{img.image_type}</td>
                  <td className="px-4 py-2 text-gray-400 font-mono text-xs truncate max-w-xs">
                    {configSummary(img.config, img.provider)}
                  </td>
                  <td className="px-4 py-2 text-gray-500 text-xs">{formatDate(img.last_scan_at)}</td>
                  <td className="px-4 py-2">
                    {img.is_stale ? (
                      <span className="text-amber-400 text-xs">Stale</span>
                    ) : (
                      <span className="text-green-400 text-xs">Current</span>
                    )}
                  </td>
                </tr>
              ))}
              {(!activeConfig?.images.length) && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                    No active base images. Run a scan or wait for startup seed.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>

        {/* RAPIDS Matrix */}
        <h4 className="text-sm font-medium text-gray-300 mb-2">RAPIDS Compatibility Matrix</h4>
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-400">
                <th className="text-left px-4 py-2 font-medium">Version</th>
                <th className="text-left px-4 py-2 font-medium">Python</th>
                <th className="text-left px-4 py-2 font-medium">CUDA</th>
                <th className="text-left px-4 py-2 font-medium">Default</th>
              </tr>
            </thead>
            <tbody>
              {activeConfig?.rapids_versions.map(rv => (
                <tr key={rv.id} className="border-b border-gray-800/50">
                  <td className="px-4 py-2 text-gray-200 font-mono">{rv.rapids_version}</td>
                  <td className="px-4 py-2 text-gray-400 font-mono text-xs">
                    {rv.compatible_python.join(', ')}
                  </td>
                  <td className="px-4 py-2 text-gray-400 font-mono text-xs">
                    {rv.compatible_cuda.join(', ')}
                  </td>
                  <td className="px-4 py-2">
                    {rv.is_default && (
                      <span className="text-blue-400 text-xs font-medium">Default</span>
                    )}
                  </td>
                </tr>
              ))}
              {(!activeConfig?.rapids_versions.length) && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                    No active RAPIDS versions.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Section 2: Pending Proposals                                        */}
      {/* ------------------------------------------------------------------ */}
      {(proposals.length > 0 || rapidsProposals.length > 0) && (
        <div>
          <h3 className="text-lg font-medium text-white mb-4">Pending Proposals</h3>

          {/* Consistency warnings */}
          {activeConfig?.latest_scan?.consistency_warnings && activeConfig.latest_scan.consistency_warnings.length > 0 && (
            <div className="bg-amber-900/20 border border-amber-800 rounded-lg p-3 mb-4">
              <p className="text-amber-300 text-sm font-medium mb-1">Consistency Warnings</p>
              {activeConfig.latest_scan.consistency_warnings.map((w, i) => (
                <p key={i} className="text-amber-400/80 text-xs">{w}</p>
              ))}
            </div>
          )}

          {/* Base Image Proposals */}
          {proposals.length > 0 && (
            <Card className="overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-400">
                    <th className="text-left px-4 py-2 font-medium">Provider</th>
                    <th className="text-left px-4 py-2 font-medium">Image Type</th>
                    <th className="text-left px-4 py-2 font-medium">Changes</th>
                    <th className="text-left px-4 py-2 font-medium">Discovered</th>
                    <th className="text-right px-4 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.map(p => (
                    <tr key={p.id} className="border-b border-gray-800/50">
                      <td className="px-4 py-2 text-gray-300 uppercase text-xs font-mono">{p.provider}</td>
                      <td className="px-4 py-2 text-gray-200">{p.image_type}</td>
                      <td className="px-4 py-2 text-gray-400 text-xs max-w-sm truncate">
                        {p.diff_summary || 'New config'}
                      </td>
                      <td className="px-4 py-2 text-gray-500 text-xs">{formatDate(p.discovered_at)}</td>
                      <td className="px-4 py-2 text-right space-x-2">
                        <Button
                          size="sm"
                          className="bg-green-700 hover:bg-green-600"
                          onClick={() => setConfirmAction({
                            type: 'approve', target: 'image', id: p.id,
                            label: `${p.provider}/${p.image_type}`,
                          })}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmAction({
                            type: 'reject', target: 'image', id: p.id,
                            label: `${p.provider}/${p.image_type}`,
                          })}
                        >
                          Reject
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* RAPIDS Proposals */}
          {rapidsProposals.length > 0 && (
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-400">
                    <th className="text-left px-4 py-2 font-medium">Version</th>
                    <th className="text-left px-4 py-2 font-medium">Python</th>
                    <th className="text-left px-4 py-2 font-medium">CUDA</th>
                    <th className="text-left px-4 py-2 font-medium">New?</th>
                    <th className="text-left px-4 py-2 font-medium">Changes</th>
                    <th className="text-right px-4 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rapidsProposals.map(p => (
                    <tr key={p.id} className="border-b border-gray-800/50">
                      <td className="px-4 py-2 text-gray-200 font-mono">{p.rapids_version}</td>
                      <td className="px-4 py-2 text-gray-400 font-mono text-xs">
                        {p.compatible_python.join(', ')}
                      </td>
                      <td className="px-4 py-2 text-gray-400 font-mono text-xs">
                        {p.compatible_cuda.join(', ')}
                      </td>
                      <td className="px-4 py-2">
                        {p.is_new_version && (
                          <span className="text-green-400 text-xs">New</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-400 text-xs max-w-sm truncate">
                        {p.diff_summary}
                      </td>
                      <td className="px-4 py-2 text-right space-x-2">
                        <Button
                          size="sm"
                          className="bg-green-700 hover:bg-green-600"
                          onClick={() => setConfirmAction({
                            type: 'approve', target: 'rapids', id: p.id,
                            label: `RAPIDS ${p.rapids_version}`,
                          })}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmAction({
                            type: 'reject', target: 'rapids', id: p.id,
                            label: `RAPIDS ${p.rapids_version}`,
                          })}
                        >
                          Reject
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Section 3: Scan History (collapsible)                               */}
      {/* ------------------------------------------------------------------ */}
      <div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors"
        >
          <ChevronRight className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
          Scan History ({scans.length})
        </button>

        {showHistory && (
          <Card className="mt-3 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-gray-400">
                  <th className="text-left px-4 py-2 font-medium">ID</th>
                  <th className="text-left px-4 py-2 font-medium">Trigger</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                  <th className="text-left px-4 py-2 font-medium">Proposals</th>
                  <th className="text-left px-4 py-2 font-medium">Warnings</th>
                  <th className="text-left px-4 py-2 font-medium">Duration</th>
                  <th className="text-left px-4 py-2 font-medium">Time</th>
                </tr>
              </thead>
              <tbody>
                {scans.map(s => (
                  <tr key={s.id} className="border-b border-gray-800/50">
                    <td className="px-4 py-2 text-gray-500 font-mono text-xs">#{s.id}</td>
                    <td className="px-4 py-2 text-gray-400 text-xs">{s.trigger_type}</td>
                    <td className={`px-4 py-2 text-xs font-medium ${statusBadge(s.status)}`}>
                      {s.status}
                    </td>
                    <td className="px-4 py-2 text-gray-400 text-xs">
                      {s.proposals_created} img + {s.rapids_proposals_created} rapids
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {s.consistency_warnings?.length ? (
                        <span className="text-amber-400">{s.consistency_warnings.length}</span>
                      ) : (
                        <span className="text-gray-600">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">
                      {s.duration_seconds != null ? `${s.duration_seconds.toFixed(1)}s` : '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{formatDate(s.started_at)}</td>
                  </tr>
                ))}
                {scans.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                      No scans yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Confirm Dialog                                                      */}
      {/* ------------------------------------------------------------------ */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card variant="glass" className="p-6 max-w-md w-full mx-4">
            <h4 className="text-white font-medium mb-2">
              {confirmAction.type === 'approve' ? 'Approve' : 'Reject'} {confirmAction.label}?
            </h4>
            <p className="text-gray-400 text-sm mb-4">
              {confirmAction.type === 'approve'
                ? 'This will update the active configuration used by Packer builds.'
                : 'This proposal will be discarded.'}
            </p>

            {confirmAction.type === 'approve' && confirmAction.target === 'rapids' && (
              <label className="flex items-center gap-2 mb-3 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={setAsDefault}
                  onChange={e => setSetAsDefault(e.target.checked)}
                  className="rounded bg-gray-800 border-gray-600"
                />
                Set as default RAPIDS version
              </label>
            )}

            <textarea
              value={reviewNotes}
              onChange={e => setReviewNotes(e.target.value)}
              placeholder="Notes (optional)"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 mb-4 resize-none"
              rows={2}
            />

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setConfirmAction(null);
                  setReviewNotes('');
                  setSetAsDefault(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className={
                  confirmAction.type === 'approve'
                    ? 'bg-green-700 hover:bg-green-600'
                    : 'bg-red-700 hover:bg-red-600'
                }
                onClick={executeAction}
              >
                {confirmAction.type === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
